import { AsyncComponent } from 'vue'

export type CmsAsyncComponents = Record<string, AsyncComponent>

const cmsComponentLoaders = {
  html: () => import('~/components/organisms/grid/shared/OneCmsDynamicComponent.vue'),
  staticHtml: () => import('~/components/organisms/grid/shared/OneCmsStaticComponent.vue'),
  image: () => import('~/components/molecules/images/OneCmsGridImageComponent.vue'),
  link: () => import('~/components/molecules/links/OneCmsGridLinkComponent.vue'),
  textEditor: () => import('~/components/organisms/grid/shared/OneCmsTextEditorComponent.vue'),
  shopForm: () => import('~/components/organisms/grid/static/OneShopForm.vue'),
  pluginSlot: () => import('~/components/organisms/grid/shared/OnePluginSlotComponent.vue'),
  productContainer: () => import('~/components/organisms/grid/content/OneCmsProductContainer.vue'),
  productsListing: () => import('~/components/organisms/grid/static/OneProductsListing.vue'),
  Category: () => import('~/components/organisms/grid/content/OneCmsCategoryOverview.vue'),
  TopProducts: () => import('~/components/organisms/grid/content/OneCmsTopProducts.vue'),
  StaticBanners: () => import('~/components/organisms/grid/content/OneCmsStaticBanners.vue'),
  carouselWithCategories: () =>
    import('~/components/organisms/grid/content/OneCmsCategoriesListWithBanner.vue'),
  customizableProductsRotator: () =>
    import('~/components/organisms/grid/content/OneCmsProductsRotator.vue'),
  carouselWithThumbnails: () =>
    import('~/components/organisms/grid/content/OneCmsCarouselWithThumbnails.vue'),
  youtube: () => import('~/components/organisms/grid/shared/OneCmsYoutubeComponent.vue'),
  codesScanner: () => import('~/components/organisms/shared/OneBarcodeScanner.vue'),
  gridArticle: () => import('~/components/organisms/grid/content/OneCmsArticle.vue'),
  productPack: () => import('~/components/organisms/grid/content/OneCmsProductPack.vue'),
  blogModule: () => import('~/components/organisms/grid/shared/OneBlogModuleComponent.vue'),
  widgetSeparator: () => import('~/components/atoms/OneCmsWidgetSeparator.vue'),
  listWithMegapopup: () => import('~/components/organisms/grid/content/OneCmsCategoriesMegadropdown.vue'),
  list: () => import('~/components/molecules/lists/OneCmsGridList.vue'),
  collapse: () => import('~/components/molecules/lists/OneCmsGridCollapse.vue'),
  categoriesDropdown: () =>
    import('~/components/organisms/grid/header/OneCategoriesDropdownComponent.vue'),
  brandsDropdown: () =>
    import('~/components/organisms/grid/header/OneBrandsDropdownComponent.vue'),
  cartIcon: () => import('~/components/organisms/grid/header/OneCartIconComponent.vue'),
  wishlistIcon: () => import('~/components/organisms/grid/header/OneWishlistIconComponent.vue'),
  search: () => import('~/components/organisms/grid/header/OneSearchComponent.vue'),
  taxSwitcher: () => import('~/components/organisms/grid/header/OneTaxSwitcherComponent.vue'),
  pricesSwitcher: () => import('~/components/organisms/grid/header/OneHidePriceSwitcherComponent.vue'),
  warehouses: () => import('~/components/organisms/grid/header/OneWarehouseSelectorComponent.vue'),
  accountDropdown: () => import('~/components/organisms/grid/header/OneAccountDropdownComponent.vue'),
  cartValue: () => import('~/components/organisms/grid/header/OneCartValueComponent.vue'),
  loginLogout: () => import('~/components/organisms/grid/header/OneLoginLogoutComponent.vue'),
  managerInfo: () => import('~/components/organisms/grid/header/OneManagerInfoComponent.vue'),
  productGallery: () => import('~/components/organisms/grid/product/OneGallery.vue'),
  productDescription: () =>
    import('~/components/organisms/grid/product/OneProductDescriptionSection.vue'),
  productTechnicalInformation: () =>
    import('~/components/organisms/grid/product/OneProductTechnicalDataSection.vue'),
  productStocks: () => import('~/components/organisms/grid/product/OneProductStocks.vue'),
  productActions: () => import('~/components/organisms/grid/product/OneProductActions.vue'),
  productFiles: () => import('~/components/organisms/grid/product/OneProductFilesSection.vue'),
  productRelations: () => import('~/components/organisms/grid/product/OneProductRelations.vue'),
  relatedProducts: () =>
    import('~/components/organisms/grid/product/OneRelatedProductsGridWrapper.vue'),
  recommendedProducts: () =>
    import('~/components/organisms/grid/product/OneRecommendedProductsGridWrapper.vue'),
  productTitleWithLabels: () =>
    import('~/components/organisms/grid/product/OneProductTitleWithLabels.vue'),
  euEnergyLabel: () => import('~/components/organisms/grid/product/OneEuEnergyLabel.vue'),
}

const commonCmsComponents: CmsAsyncComponents = {
  html: cmsComponentLoaders.html,
  staticHtml: cmsComponentLoaders.staticHtml,
  image: cmsComponentLoaders.image,
  link: cmsComponentLoaders.link,
  textEditor: cmsComponentLoaders.textEditor,
  shopForm: cmsComponentLoaders.shopForm,
}

const generalPageCmsComponents: CmsAsyncComponents = {
  ...commonCmsComponents,
  pluginSlot: cmsComponentLoaders.pluginSlot,
  productContainer: cmsComponentLoaders.productContainer,
  Category: cmsComponentLoaders.Category,
  TopProducts: cmsComponentLoaders.TopProducts,
  StaticBanners: cmsComponentLoaders.StaticBanners,
  carouselWithCategories: cmsComponentLoaders.carouselWithCategories,
  customizableProductsRotator: cmsComponentLoaders.customizableProductsRotator,
  carouselWithThumbnails: cmsComponentLoaders.carouselWithThumbnails,
  youtube: cmsComponentLoaders.youtube,
  gridArticle: cmsComponentLoaders.gridArticle,
  productPack: cmsComponentLoaders.productPack,
}

export const createIndexCmsComponents = (): CmsAsyncComponents => ({
  ...generalPageCmsComponents,
  blogModule: cmsComponentLoaders.blogModule,
  listWithMegapopup: cmsComponentLoaders.listWithMegapopup,
  widgetSeparator: cmsComponentLoaders.widgetSeparator,
})

export const createStaticPageCmsComponents = (): CmsAsyncComponents => ({
  ...generalPageCmsComponents,
  productsListing: cmsComponentLoaders.productsListing,
  codesScanner: cmsComponentLoaders.codesScanner,
})

export const createHeaderCmsComponents = (): CmsAsyncComponents => ({
  ...commonCmsComponents,
  categoriesDropdown: cmsComponentLoaders.categoriesDropdown,
  brandsDropdown: cmsComponentLoaders.brandsDropdown,
  cartIcon: cmsComponentLoaders.cartIcon,
  wishlistIcon: cmsComponentLoaders.wishlistIcon,
  search: cmsComponentLoaders.search,
  taxSwitcher: cmsComponentLoaders.taxSwitcher,
  pricesSwitcher: cmsComponentLoaders.pricesSwitcher,
  warehouses: cmsComponentLoaders.warehouses,
  accountDropdown: cmsComponentLoaders.accountDropdown,
  cartValue: cmsComponentLoaders.cartValue,
  loginLogout: cmsComponentLoaders.loginLogout,
  managerInfo: cmsComponentLoaders.managerInfo,
  codesScanner: cmsComponentLoaders.codesScanner,
})

export const createFooterCmsComponents = (): CmsAsyncComponents => ({
  ...commonCmsComponents,
  list: cmsComponentLoaders.list,
  collapse: cmsComponentLoaders.collapse,
  codesScanner: cmsComponentLoaders.codesScanner,
})

export const createProductCmsComponents = (): CmsAsyncComponents => ({
  ...commonCmsComponents,
  pluginSlot: cmsComponentLoaders.pluginSlot,
  youtube: cmsComponentLoaders.youtube,
  productGallery: cmsComponentLoaders.productGallery,
  productDescription: cmsComponentLoaders.productDescription,
  productTechnicalInformation: cmsComponentLoaders.productTechnicalInformation,
  productStocks: cmsComponentLoaders.productStocks,
  productActions: cmsComponentLoaders.productActions,
  productFiles: cmsComponentLoaders.productFiles,
  productRelations: cmsComponentLoaders.productRelations,
  relatedProducts: cmsComponentLoaders.relatedProducts,
  recommendedProducts: cmsComponentLoaders.recommendedProducts,
  productTitleWithLabels: cmsComponentLoaders.productTitleWithLabels,
  euEnergyLabel: cmsComponentLoaders.euEnergyLabel,
})
